import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePaymentsStatus } from "../lib/payments";
import type {
  Order,
  PaymentMethod,
  PaymentsPanel as PaymentsPanelData,
  RecordedPayment,
} from "../lib/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  Badge,
  Button,
  Card,
  CommissionStatusBadge,
  DetailRow,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  OrderStatusBadge,
  Select,
  StatusBadge,
  SubscriptionStatusBadge,
  Table,
  TableWrap,
  Td,
  Th,
  centsFromInput,
  formatDate,
  formatMoney,
  inputFromCents,
} from "../components/ui";

export function OrderDetailRoute() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const role = user?.role ?? "customer";
  const isAdmin = role === "admin";
  const queryClient = useQueryClient();

  const [confirming, setConfirming] = useState<"paid" | "cancel" | "refund" | "renew" | null>(null);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [keyEmailed, setKeyEmailed] = useState(false);
  /** Null until a reminder is attempted; then whether it actually left. */
  const [reminded, setReminded] = useState<"sent" | "unconfigured" | null>(null);
  const [payLink, setPayLink] = useState<string | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [clawback, setClawback] = useState<number | null>(null);
  const { cardsAccepted } = usePaymentsStatus();

  const order = useQuery({
    queryKey: ["order", id],
    queryFn: () => api<Order>(`/api/v1/admin/orders/${id}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["order", id] });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["commission"] });
    void queryClient.invalidateQueries({ queryKey: ["licenses"] });
  };

  const markPaid = useMutation({
    mutationFn: () =>
      api<{ order: Order; licenseKey: string | null; keyEmailed: boolean }>(
        `/api/v1/admin/orders/${id}/paid`,
        { method: "POST", body: {} },
      ),
    onSuccess: (result) => {
      setConfirming(null);
      setIssuedKey(result.licenseKey);
      setKeyEmailed(result.keyEmailed);
      invalidate();
    },
  });

  const cancel = useMutation({
    mutationFn: () => api(`/api/v1/admin/orders/${id}/cancel`, { method: "POST", body: {} }),
    onSuccess: () => {
      setConfirming(null);
      invalidate();
    },
  });

  /**
   * Nudges the buyer. Deliberately a button and not a schedule: an automatic
   * chase can reach somebody who walked away on purpose, and this way it is a
   * thing a person decided was worth sending.
   */
  const remind = useMutation({
    mutationFn: () =>
      api<{ sent: boolean }>(`/api/v1/admin/orders/${id}/remind`, { method: "POST", body: {} }),
    onSuccess: (result) => {
      setReminded(result.sent ? "sent" : "unconfigured");
      invalidate();
    },
  });

  const refund = useMutation({
    mutationFn: () =>
      api<{ clawbackOwed: boolean; clawbackCents: number }>(`/api/v1/admin/orders/${id}/refund`, {
        method: "POST",
        body: { ...(refundReason.trim() === "" ? {} : { reason: refundReason.trim() }) },
      }),
    onSuccess: (result) => {
      setConfirming(null);
      setRefundReason("");
      // Surfaced rather than filed away: no code here can pull back a transfer
      // that already left the bank, so somebody has to be told.
      setClawback(result.clawbackOwed ? result.clawbackCents : null);
      invalidate();
    },
  });

  const billingPortal = useMutation({
    mutationFn: () =>
      api<{ url: string }>("/api/v1/admin/billing-portal", {
        method: "POST",
        // A customer's own id always wins server-side, so sending one here
        // would be theatre. Staff opening a customer's page must name them.
        body: role === "customer" ? {} : { customerId: order.data?.customer.id },
      }),
    // Stripe's page, in this tab. Coming back lands on the orders list.
    onSuccess: (result) => window.location.assign(result.url),
  });

  const startCheckout = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/api/v1/admin/orders/${id}/checkout`, { method: "POST", body: {} }),
    onSuccess: (result) => {
      // A customer paying their own order goes straight there. Staff get the
      // URL to send on, because the person with the card is not at this screen.
      if (role === "customer") window.location.assign(result.url);
      else setPayLink(result.url);
    },
  });

  const renew = useMutation({
    mutationFn: (subscriptionId: string) =>
      api(`/api/v1/admin/subscriptions/${subscriptionId}/renew`, { method: "POST", body: {} }),
    onSuccess: () => {
      setConfirming(null);
      invalidate();
    },
  });

  if (order.isPending) return <p className="text-sm text-dim">Loading…</p>;
  if (order.isError) {
    const missing = order.error instanceof ApiError && order.error.status === 404;
    return (
      <div className="space-y-4">
        <ErrorNote>{missing ? "No such order." : "Could not load this order."}</ErrorNote>
        <Link to="/orders" className="text-sm text-accent underline underline-offset-4">
          Back to orders
        </Link>
      </div>
    );
  }

  const data = order.data;
  const maintenance = formatMoney(data.plan.priceCents, data.plan.currency ?? data.currency);

  return (
    <div className="space-y-6">
      <nav>
        <Link to="/orders" className="text-sm text-dim underline-offset-4 hover:underline">
          ← Orders
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="tabular text-xl text-ink">{data.number}</h1>
          <p className="text-sm text-dim">
            {data.customer.name} · {data.product.name} · {data.plan.name}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <OrderStatusBadge status={data.status} />
          {cardsAccepted && data.status === "awaiting_payment" && (
            <Button
              variant={role === "customer" ? "primary" : "secondary"}
              disabled={startCheckout.isPending}
              onClick={() => startCheckout.mutate()}
            >
              {startCheckout.isPending
                ? "Preparing…"
                : role === "customer"
                  ? "Pay now"
                  : "Card payment link"}
            </Button>
          )}
          {isAdmin && data.status === "awaiting_payment" && (
            <>
              <Button disabled={remind.isPending} onClick={() => remind.mutate()}>
                {remind.isPending ? "Sending…" : "Send reminder"}
              </Button>
              <Button variant="primary" onClick={() => setConfirming("paid")}>
                Record payment
              </Button>
              <Button variant="danger" onClick={() => setConfirming("cancel")}>
                Cancel
              </Button>
            </>
          )}
          {isAdmin && data.status === "paid" && (
            <Button variant="danger" onClick={() => setConfirming("refund")}>
              Refund
            </Button>
          )}
        </div>
      </header>

      {startCheckout.isError && <ErrorNote>{(startCheckout.error as Error).message}</ErrorNote>}
      {remind.isError && <ErrorNote>{(remind.error as Error).message}</ErrorNote>}
      {/* Said plainly either way. "Sent" when nothing left would have the admin
          waiting on a reply that was never asked for. */}
      {reminded === "sent" && (
        <p className="text-sm text-live">Reminder sent to {data.customer.email}.</p>
      )}
      {reminded === "unconfigured" && (
        <ErrorNote>
          Nothing was sent — no mail provider is connected. The order is still waiting.
        </ErrorNote>
      )}

      {/* Nothing in this system can pull a bank transfer back, so the only
          useful thing it can do is refuse to let this go unnoticed. */}
      {clawback !== null && (
        <Card title="Commission already paid">
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              {data.marketer?.name ?? "The marketer"} was paid{" "}
              <span className="tabular text-ink">{formatMoney(clawback, data.currency)}</span> for
              this sale before it was refunded. The ledger still shows it as paid, because it was —
              recovering it is a conversation, not something this screen can do.
            </p>
            <Button onClick={() => setClawback(null)}>Understood</Button>
          </div>
        </Card>
      )}

      {payLink !== null && (
        <Card title="Card payment link">
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              Send this to the customer. Paying it issues the licence automatically — there is no
              need to come back and record it here.
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-ink">
              {payLink}
            </code>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => void navigator.clipboard.writeText(payLink)}>
                Copy link
              </Button>
              <Button onClick={() => setPayLink(null)}>Done</Button>
            </div>
          </div>
        </Card>
      )}

      {/* The key exists in readable form exactly once. Anything that could
          navigate away from it before it is copied is a way to lose it. */}
      {issuedKey !== null && (
        <Card title="Licence issued">
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              {keyEmailed
                ? "This has been emailed to the customer. It is stored only as a hash, so this is the last time anyone here can read it."
                : // Says what has to happen next rather than what usually
                  // happens. Nobody sent this yet.
                  "Copy this key now and send it to the customer — it has not been emailed. It is stored only as a hash, so this is the last time anyone can read it."}
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-base text-ink">
              {issuedKey}
            </code>
            {/* Wraps rather than overflowing: on the narrowest screen the copy
                button is the one control that must stay reachable. */}
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => void navigator.clipboard.writeText(issuedKey)}>
                Copy key
              </Button>
              <Button onClick={() => setIssuedKey(null)}>I have saved it</Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="The sale">
          <dl className="divide-y divide-hairline text-sm">
            <DetailRow label="Customer">
              <span className="block">{data.customer.name}</span>
              <span className="block text-xs text-dim">{data.customer.email}</span>
              {data.customer.phone != null && (
                <span className="block text-xs text-dim">{data.customer.phone}</span>
              )}
            </DetailRow>
            <DetailRow label="One-time price">
              <span className="tabular">{formatMoney(data.oneTimeCents, data.currency)}</span>
            </DetailRow>
            <DetailRow label="Maintenance">
              <span className="tabular">
                {maintenance} / {data.plan.interval}
              </span>
            </DetailRow>
            <DetailRow label="Hosting">
              <Badge tone={data.deployment === "managed" ? "accent" : "quiet"}>
                {data.deployment === "managed" ? "We host it" : "Customer hosts it"}
              </Badge>
            </DetailRow>
            <DetailRow label="Sold by">
              {data.marketer === null ? (
                <span className="text-dim">Direct</span>
              ) : (
                <>
                  <span className="block">{data.marketer.name}</span>
                  <span className="block text-xs text-dim">{data.marketer.email}</span>
                </>
              )}
            </DetailRow>
            <DetailRow label="Created">{formatDate(data.createdAt)}</DetailRow>
            <DetailRow label="Paid">{formatDate(data.paidAt)}</DetailRow>
          </dl>
        </Card>

        <div className="space-y-6">
          <Card
            title="Maintenance subscription"
            action={
              isAdmin && data.subscription !== null ? (
                <Button onClick={() => setConfirming("renew")}>Record a renewal</Button>
              ) : undefined
            }
          >
            {data.subscription === null ? (
              <EmptyState>
                Nothing yet. A subscription opens when this order is marked paid.
              </EmptyState>
            ) : (
              <dl className="divide-y divide-hairline text-sm">
                <DetailRow label="Status">
                  <SubscriptionStatusBadge status={data.subscription.status} />
                </DetailRow>
                <DetailRow label="Amount">
                  <span className="tabular">
                    {formatMoney(data.subscription.amountCents, data.subscription.currency)} /{" "}
                    {data.subscription.interval}
                  </span>
                </DetailRow>
                <DetailRow label={role === "customer" ? "Next charge" : "Paid until"}>
                  {formatDate(data.subscription.currentPeriodEnd)}
                </DetailRow>
                {data.subscription.cancelAtPeriodEnd && (
                  <DetailRow label="Ending">
                    <Badge tone="pending">Cancels at period end</Badge>
                  </DetailRow>
                )}
              </dl>
            )}

            {/* Card details are changed on Stripe's pages, never here. The
                alternative is a card number arriving by email. */}
            {cardsAccepted && data.subscription !== null && (
              <div className="space-y-2 border-t border-hairline p-4">
                <Button
                  disabled={billingPortal.isPending}
                  onClick={() => billingPortal.mutate()}
                >
                  {billingPortal.isPending ? "Opening…" : "Update card and invoices"}
                </Button>
                {billingPortal.isError && (
                  <ErrorNote>{(billingPortal.error as Error).message}</ErrorNote>
                )}
              </div>
            )}
          </Card>

          <Card title="Licence">
            {data.license === null ? (
              <EmptyState>Issued when this order is marked paid.</EmptyState>
            ) : (
              <dl className="divide-y divide-hairline text-sm">
                <DetailRow label="Key">
                  <Link
                    to={`/licenses/${data.license.id}`}
                    className="tabular text-accent underline-offset-4 hover:underline"
                  >
                    {data.product.code}-••••-{data.license.keyLast4}
                  </Link>
                </DetailRow>
                <DetailRow label="Status">
                  <StatusBadge status={data.license.status} />
                </DetailRow>
                <DetailRow label="Expires">{formatDate(data.license.expiresAt)}</DetailRow>
              </dl>
            )}
          </Card>

          {role !== "customer" && data.commission !== null && (
            <Card title="Commission">
              <dl className="divide-y divide-hairline text-sm">
                <DetailRow label="Amount">
                  <span className="tabular">
                    {formatMoney(data.commission.amountCents, data.commission.currency)}
                  </span>
                </DetailRow>
                <DetailRow label="Status">
                  <CommissionStatusBadge status={data.commission.status} />
                </DetailRow>
                <DetailRow label="Paid out">{formatDate(data.commission.paidAt)}</DetailRow>
              </dl>
            </Card>
          )}
        </div>
      </div>

      <PaymentsPanel
        order={data}
        isAdmin={isAdmin}
        onIssued={(licenseKey, emailed) => {
          // The same card the mark-paid flow uses. A key has one readable
          // moment, and two different ways of showing it is two ways to lose it.
          setIssuedKey(licenseKey);
          setKeyEmailed(emailed);
          invalidate();
        }}
      />

      {data.hostnames.length > 0 && (
        <Card title="Hostnames on this order">
          <ul className="divide-y divide-hairline">
            {data.hostnames.map((hostname) => (
              <li key={hostname} className="px-4 py-2.5 tabular">
                {hostname}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {isAdmin && data.notes != null && (
        <Card title="Notes">
          <p className="whitespace-pre-wrap px-4 py-3 text-sm text-muted">{data.notes}</p>
        </Card>
      )}

      <ConfirmDialog
        open={confirming === "paid"}
        title="Record this payment"
        confirmLabel="Record payment"
        confirmVariant="primary"
        pending={markPaid.isPending}
        error={markPaid.isError ? (markPaid.error as Error).message : null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => markPaid.mutate()}
        description={
          <>
            <p>
              This issues the licence, opens the maintenance subscription, and{" "}
              {data.marketer === null
                ? "records no commission, because this is a direct sale."
                : `records ${formatMoney(data.commissionCents, data.currency)} owed to ${data.marketer.name}.`}
            </p>
            <p>
              The licence key is shown once, immediately after this. Only its hash is kept, so it
              cannot be recovered afterwards.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={confirming === "cancel"}
        title="Cancel this order"
        confirmLabel="Cancel order"
        pending={cancel.isPending}
        error={cancel.isError ? (cancel.error as Error).message : null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => cancel.mutate()}
        description={
          <p>
            Nothing was issued for it, so nothing is withdrawn. The order stays on record as
            cancelled rather than disappearing.
          </p>
        }
      />

      <ConfirmDialog
        open={confirming === "refund"}
        title="Refund this order"
        confirmLabel="Refund and revoke"
        confirmPhrase={data.number}
        pending={refund.isPending}
        error={refund.isError ? (refund.error as Error).message : null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => refund.mutate()}
        description={
          <>
            <p>
              The licence is revoked and stops working, the maintenance subscription ends, and{" "}
              {data.commission === null
                ? "there is no commission to unwind."
                : data.commission.status === "paid"
                  ? `the ${formatMoney(data.commission.amountCents, data.commission.currency)} already paid to ${data.marketer?.name ?? "the marketer"} stays on the ledger — you will need to claw it back yourself.`
                  : `the ${formatMoney(data.commission.amountCents, data.commission.currency)} owed to ${data.marketer?.name ?? "the marketer"} is voided.`}
            </p>
            <p>
              This does not move any money. Refund the card in Stripe, or reverse the transfer at
              the bank — this records that you did and withdraws what it bought.
            </p>
            <div className="space-y-1.5">
              <label className="field-label" htmlFor="refund-reason">
                Reason (optional — the customer reads this)
              </label>
              <Input
                id="refund-reason"
                value={refundReason}
                maxLength={500}
                placeholder="Cancelled within the trial period."
                onChange={(event) => setRefundReason(event.target.value)}
              />
            </div>
          </>
        }
      />

      <ConfirmDialog
        open={confirming === "renew"}
        title="Record a renewal"
        confirmLabel="Extend the subscription"
        confirmVariant="primary"
        pending={renew.isPending}
        error={renew.isError ? (renew.error as Error).message : null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (data.subscription !== null) renew.mutate(data.subscription.id);
        }}
        description={
          <>
            <p>
              Adds one {data.subscription?.interval ?? "period"} from{" "}
              {formatDate(data.subscription?.currentPeriodEnd)} and moves the licence's expiry with
              it. Renewing early does not lose the time already paid for.
            </p>
            <p>
              This records a payment taken some other way. It does not charge a card — nothing here
              moves money.
            </p>
          </>
        }
      />
    </div>
  );
}

/** Everything except a card, which records itself when Stripe reports it. */
const METHODS: Array<{ value: PaymentMethod; label: string }> = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

const METHOD_LABELS: Record<PaymentMethod, string> = {
  stripe: "Card",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  cheque: "Cheque",
  other: "Other",
};

/**
 * How the money actually arrived, and what is still owed.
 *
 * An order can take several payments — a deposit and a balance, a transfer that
 * came in two parts — so what matters most here is the outstanding figure.
 * Recording a part payment provisions nothing at all; the payment that closes
 * the balance is the one that issues the licence, and it says so before it is
 * confirmed rather than afterwards.
 */
function PaymentsPanel({
  order,
  isAdmin,
  onIssued,
}: {
  order: Order;
  isAdmin: boolean;
  onIssued: (licenseKey: string, keyEmailed: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("bank_transfer");
  const [reference, setReference] = useState("");
  const [receivedAt, setReceivedAt] = useState("");

  const panel = useQuery({
    queryKey: ["payments", order.id],
    queryFn: () => api<PaymentsPanelData>(`/api/v1/admin/orders/${order.id}/payments`),
  });

  const amountCents = centsFromInput(amount);
  const summary = panel.data?.summary;
  // Deliberately not "would take it past the total": an overpayment settles the
  // order too, and the confirmation has to say what it will actually do.
  const settles =
    summary !== undefined &&
    !summary.settled &&
    amountCents !== undefined &&
    amountCents >= summary.outstandingCents;

  const close = () => {
    setConfirming(false);
    setRecording(false);
    setAmount("");
    setReference("");
    setReceivedAt("");
  };

  const record = useMutation({
    mutationFn: () =>
      api<RecordedPayment>(`/api/v1/admin/orders/${order.id}/payments`, {
        method: "POST",
        body: {
          amountCents,
          method,
          ...(reference.trim().length === 0 ? {} : { reference: reference.trim() }),
          ...(receivedAt.length === 0
            ? {}
            : { receivedAt: new Date(receivedAt).toISOString() }),
        },
      }),
    onSuccess: (result) => {
      close();
      void queryClient.invalidateQueries({ queryKey: ["payments", order.id] });
      // Only the settling payment answers with a key, and only that once.
      if (result.licenseKey != null && result.licenseKey.length > 0) {
        onIssued(result.licenseKey, result.keyEmailed ?? false);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      }
    },
  });

  const canRecord = isAdmin && order.status !== "cancelled" && order.status !== "refunded";

  return (
    <Card
      title="Payments"
      action={
        canRecord && !recording ? (
          <Button
            onClick={() => {
              // Prefilled with what is left, because that is what is usually
              // being paid — and it is the figure that settles the order.
              setAmount(inputFromCents(summary?.outstandingCents));
              setRecording(true);
            }}
          >
            Record a payment
          </Button>
        ) : undefined
      }
    >
      {panel.isPending ? (
        <EmptyState>Loading…</EmptyState>
      ) : panel.isError ? (
        <div className="p-4">
          <ErrorNote>Could not load the payments on this order.</ErrorNote>
        </div>
      ) : (
        <>
          <dl className="divide-y divide-hairline border-b border-hairline text-sm">
            <DetailRow label="Order total">
              <span className="tabular">
                {formatMoney(panel.data.summary.totalCents, panel.data.summary.currency)}
              </span>
            </DetailRow>
            <DetailRow label="Received">
              <span className="tabular">
                {formatMoney(panel.data.summary.paidCents, panel.data.summary.currency)}
              </span>
            </DetailRow>
            {/* The number this panel exists for. Emphasised while anything is
                still owed, and stated plainly rather than implied by the two
                figures above it. */}
            <DetailRow label="Outstanding">
              {panel.data.summary.settled ? (
                <span className="tabular text-dim">
                  {formatMoney(0, panel.data.summary.currency)} — settled
                </span>
              ) : (
                <span className="tabular text-accent">
                  {formatMoney(
                    panel.data.summary.outstandingCents,
                    panel.data.summary.currency,
                  )}
                </span>
              )}
            </DetailRow>
          </dl>

          {panel.data.payments.length === 0 ? (
            <EmptyState>
              Nothing received yet. Record a transfer, a cheque or cash here as it arrives.
            </EmptyState>
          ) : (
            <TableWrap>
              <Table>
                <thead className="border-b border-hairline">
                  <tr>
                    <Th>Received</Th>
                    <Th>Amount</Th>
                    <Th>Method</Th>
                    <Th>Reference</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {panel.data.payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-hairline last:border-0">
                      <Td label="Received" className="text-muted">
                        {formatDate(payment.receivedAt)}
                      </Td>
                      <Td label="Amount" className="tabular">
                        {formatMoney(payment.amountCents, payment.currency)}
                      </Td>
                      <Td label="Method" className="text-muted">
                        {METHOD_LABELS[payment.method]}
                      </Td>
                      <Td label="Reference" className="text-muted">
                        {payment.reference ?? <span className="text-dim">—</span>}
                      </Td>
                      <Td label="Status">
                        <Badge tone={payment.status === "succeeded" ? "live" : "quiet"}>
                          {payment.status}
                        </Badge>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </>
      )}

      {recording && (
        <form
          className="space-y-4 border-t border-hairline p-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (amountCents !== undefined && amountCents > 0) setConfirming(true);
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Amount"
              hint={
                summary === undefined || summary.settled
                  ? "In whole currency, not cents."
                  : `${formatMoney(summary.outstandingCents, summary.currency)} outstanding.`
              }
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                required
                autoFocus
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>

            <Field label="Method" hint="A card payment records itself when Stripe reports it.">
              <Select
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              >
                {METHODS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Reference" hint="Bank reference, cheque number — anything that finds it again.">
              <Input
                value={reference}
                maxLength={200}
                placeholder="Bank transfer 8829104"
                onChange={(event) => setReference(event.target.value)}
              />
            </Field>

            <Field label="Date received" hint="When the money arrived, not when it was typed in.">
              <Input
                type="date"
                value={receivedAt}
                onChange={(event) => setReceivedAt(event.target.value)}
              />
            </Field>
          </div>

          {record.isError && <ErrorNote>{(record.error as Error).message}</ErrorNote>}

          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              disabled={record.isPending || amountCents === undefined || amountCents === 0}
            >
              {/* Not "Record payment": that is the button in the header, which
                  settles the whole order in one step. Two controls with one
                  name is how the wrong one gets pressed. */}
              Record this payment
            </Button>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={confirming}
        title={settles ? "Record the payment that settles this order" : "Record this payment"}
        confirmLabel="Record payment"
        confirmVariant="primary"
        pending={record.isPending}
        error={record.isError ? (record.error as Error).message : null}
        onCancel={() => setConfirming(false)}
        onConfirm={() => record.mutate()}
        description={
          settles ? (
            <>
              <p>
                {formatMoney(amountCents ?? 0, summary?.currency ?? order.currency)} closes the
                balance, so this fulfils the order: the licence is issued, the maintenance
                subscription opens, and{" "}
                {order.marketer === null
                  ? "no commission is recorded, because this is a direct sale."
                  : `${formatMoney(order.commissionCents, order.currency)} is recorded as owed to ${order.marketer.name}.`}
              </p>
              <p>
                The licence key is shown once, immediately after this. Only its hash is kept, so it
                cannot be recovered afterwards.
              </p>
            </>
          ) : (
            <>
              <p>
                Records {formatMoney(amountCents ?? 0, summary?.currency ?? order.currency)} against
                this order and leaves{" "}
                <span className="tabular text-ink">
                  {formatMoney(
                    Math.max(0, (summary?.outstandingCents ?? 0) - (amountCents ?? 0)),
                    summary?.currency ?? order.currency,
                  )}
                </span>{" "}
                outstanding.
              </p>
              <p>
                Nothing is issued for a part payment. No licence, no subscription and no
                commission — those wait until the balance is closed.
              </p>
            </>
          )
        }
      />
    </Card>
  );
}
